import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import AxisChartEditor from './chartEditors/AxisChartEditor'
import PieChartEditor from './chartEditors/PieChartEditor'
import TreeMapEditor from './chartEditors/TreeMapEditor'
import FunnelChartEditor from './chartEditors/FunnelChartEditor'
import RingChartEditor from './chartEditors/RingChartEditor'
import DisplayOptionsEditor from './chartEditors/DisplayOptionsEditor'
import OverridesEditor from './chartEditors/OverridesEditor'
import StyleVariantPicker from './StyleVariantPicker'

const layoutOptions = [
  { label: 'Axis Chart (Bar / Line / Scatter)', value: 'axis' },
  { label: 'Pie / Donut', value: 'pie' },
  { label: 'Treemap', value: 'treemap' },
  { label: 'Funnel', value: 'funnel' },
  { label: 'Ring / Gauge', value: 'ring' }
]

const colorThemeOptions = [
  { label: 'Primary', value: 'primary' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Accent', value: 'accent' },
  { label: 'Firm', value: 'firm' },
  { label: 'Cool', value: 'cool' },
  { label: 'Warm', value: 'warm' },
  { label: 'Default (all palettes)', value: 'default' },
  { label: 'Blue', value: 'blue' },
  { label: 'Green', value: 'green' },
  { label: 'Red', value: 'red' },
  { label: 'Purple', value: 'purple' },
  { label: 'Orange', value: 'orange' },
  { label: 'Indigo', value: 'indigo' },
  { label: 'Cyan', value: 'cyan' }
]

const DEFAULT_DISPLAY = {
  label: { show: true, position: null, rotate: 0, fontSize: null, format: null },
  legend: { show: false, position: 'bottom' },
  grid: { top: '10%', right: '10%', bottom: '10%', left: '10%' }
}

const DEFAULT_CONFIGS = {
  axis: {
    layout: 'axis',
    purposeCode: null,
    height: 400,
    colorTheme: 'primary',
    xAxis: { type: 'category', accessor: null, format: null, name: null, position: 'bottom', boundaryGap: null, labelRotate: null, labelInterval: null },
    yAxes: [{ axisId: 'left', type: 'value', format: null, name: null, position: 'left', padFactor: 0, labelRotate: null }],
    tooltip: { trigger: 'axis', valueFormat: null },
    series: [],
    display: DEFAULT_DISPLAY,
    overrides: null
  },
  pie: {
    layout: 'pie',
    purposeCode: null,
    height: 400,
    colorTheme: 'primary',
    nameAccessor: null,
    valueAccessor: null,
    radius: '75%',
    innerRadius: '0%',
    roseType: null,
    tooltip: { trigger: 'item', valueFormat: null },
    display: DEFAULT_DISPLAY,
    overrides: null,
    seriesOverrides: null
  },
  treemap: {
    layout: 'treemap',
    purposeCode: null,
    height: 400,
    colorTheme: 'primary',
    indexBy: [],
    valueAccessor: null,
    colorAccessor: null,
    rootLabel: 'All',
    tooltip: { trigger: 'item', valueFormat: null },
    display: DEFAULT_DISPLAY,
    overrides: null,
    seriesOverrides: null
  },
  funnel: {
    layout: 'funnel',
    purposeCode: null,
    height: 400,
    colorTheme: 'primary',
    nameAccessor: null,
    valueAccessor: null,
    sort: 'descending',
    orient: 'vertical',
    gap: 0,
    funnelAlign: 'center',
    tooltip: { trigger: 'item', valueFormat: null },
    display: DEFAULT_DISPLAY,
    overrides: null,
    seriesOverrides: null
  },
  ring: {
    layout: 'ring',
    purposeCode: null,
    height: 300,
    colorTheme: 'primary',
    width: 300,
    metrics: [],
    valueFormat: 'percentage'
  }
}

const LAYOUT_EDITORS = {
  axis: AxisChartEditor,
  pie: PieChartEditor,
  treemap: TreeMapEditor,
  funnel: FunnelChartEditor,
  ring: RingChartEditor
}

function ChartConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const columns = useMemo(() => {
    if (!localConfig.purposeCode || !allPurposes[localConfig.purposeCode]) return []
    return allPurposes[localConfig.purposeCode].columns || []
  }, [localConfig.purposeCode, allPurposes])

  const handleLayoutChange = useCallback((layout) => {
    const defaults = DEFAULT_CONFIGS[layout]
    if (!defaults) return
    setLocalConfig({ ...defaults, purposeCode: localConfig.purposeCode })
  }, [localConfig.purposeCode])

  const handlePurposeChange = useCallback((purposeCode) => {
    setLocalConfig(prev => ({ ...prev, purposeCode: purposeCode === '__placeholder__' ? null : purposeCode }))
  }, [])

  const handleLayoutConfigChange = useCallback((partial) => {
    setLocalConfig(prev => ({ ...prev, ...partial }))
  }, [])

  const LayoutEditor = LAYOUT_EDITORS[localConfig.layout]

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <SydLabel required label='Chart Layout'>
          <SydSelect
            minWidth='0'
            options={layoutOptions}
            value={localConfig.layout || 'axis'}
            onChange={(e) => handleLayoutChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12} md={4}>
        <SydLabel required label='Data Purpose'>
          <SydSelect
            minWidth='0'
            options={purposeOptions}
            value={localConfig.purposeCode || '__placeholder__'}
            onChange={(e) => handlePurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={6} md={2}>
        <SydLabel label='Height (px)'>
          <SydInput
            minWidth='0'
            type='number'
            value={localConfig.height || 400}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, height: Number(e.target.value) || 400 }))}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={6} md={2}>
        <SydLabel label='Color Theme'>
          <SydSelect
            minWidth='0'
            options={colorThemeOptions}
            value={localConfig.colorTheme || 'primary'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, colorTheme: e.target.value }))}
          />
        </SydLabel>
      </Grid>

      {LayoutEditor && (
        <Grid item xs={12}>
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '8px' }}>
            <LayoutEditor
              config={localConfig}
              columns={columns}
              allPurposes={allPurposes}
              onChange={handleLayoutConfigChange}
            />
          </div>
        </Grid>
      )}

      {localConfig.layout !== 'ring' && (
        <Grid item xs={12}>
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '8px' }}>
            <DisplayOptionsEditor
              display={localConfig.display}
              onChange={handleLayoutConfigChange}
            />
          </div>
        </Grid>
      )}

      {localConfig.layout !== 'ring' && (
        <Grid item xs={12}>
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '8px' }}>
            <OverridesEditor
              overrides={localConfig.overrides}
              seriesOverrides={localConfig.seriesOverrides}
              layout={localConfig.layout}
              onChange={handleLayoutConfigChange}
            />
          </div>
        </Grid>
      )}

      <StyleVariantPicker
        chartLayout={localConfig.layout || 'axis'}
        value={localConfig.styleVariant}
        onChange={(v) => setLocalConfig(prev => ({ ...prev, styleVariant: v, styleVariantParams: {} }))}
        variantParams={localConfig.styleVariantParams}
        onParamsChange={(p) => setLocalConfig(prev => ({ ...prev, styleVariantParams: p }))}
      />
    </Grid>
  )
}

ChartConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default ChartConfigEditor
